// How a month, a range of months, or a length of time is written anywhere a
// user reads one.
//
// These exist because assembling the strings inline is how you end up with a
// range that reads backwards and a duration that disagrees with its own dates.
// Nothing here is specific to employment — any month-based history reads the
// same way.
//
// Month names come from `formatMonth` in src/lib/formatters, so there is one
// month vocabulary in the app rather than two that can drift.

import { formatMonth } from 'src/lib/formatters'

import type { MonthPeriod } from './monthPeriods'
import { MONTHS_PER_YEAR } from './monthPeriods'

/**
 * The "YYYY-MM" form of a month index — what a native `<input type="month">`
 * expects, and what `formatMonth` reads.
 *
 * Note the driver application's existing month fields are masked MM/YYYY text
 * inputs (`formatMonthYear` in DriverApplicationPage/utils/formatters), not
 * native month inputs. Both can be driven from a month index; which control a
 * screen uses is that screen's decision.
 */
export const toMonthInputValue = (monthIndex: number): string => {
  const month =
    ((monthIndex % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR
  const year = Math.floor(monthIndex / MONTHS_PER_YEAR)
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(
    2,
    '0'
  )}`
}

/** Read back what that input produces. Returns null for an empty field. */
export const fromMonthInputValue = (value: string): number | null => {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  if (!match) return null
  const month = Number(match[2])
  if (month < 1 || month > MONTHS_PER_YEAR) return null
  return Number(match[1]) * MONTHS_PER_YEAR + (month - 1)
}

/**
 * The "MM/YYYY" form a driver types. Distinct from `toMonthInputValue` above:
 * that one feeds a native month input and reads year-first, this one is what
 * the application's masked month fields have always displayed.
 */
export const toMonthYearValue = (monthIndex: number): string => {
  const month =
    ((monthIndex % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR
  const year = Math.floor(monthIndex / MONTHS_PER_YEAR)
  return `${String(month + 1).padStart(2, '0')}/${String(year).padStart(
    4,
    '0'
  )}`
}

/** Read "MM/YYYY" back. Returns null for anything incomplete or impossible. */
export const fromMonthYearValue = (value: string): number | null => {
  const match = /^(\d{2})\/(\d{4})$/.exec(value.trim())
  if (!match) return null
  const month = Number(match[1])
  if (month < 1 || month > MONTHS_PER_YEAR) return null
  return Number(match[2]) * MONTHS_PER_YEAR + (month - 1)
}

/** "Mar 2020" */
export const formatMonthIndex = (monthIndex: number): string =>
  formatMonth(toMonthInputValue(monthIndex), 'long')

/**
 * "Mar 2020 – Present" — always earlier to later, never reversed to match the
 * backwards order in which people fill these in.
 */
export const formatMonthRange = (
  start: number,
  end: number | null | undefined
): string =>
  `${formatMonthIndex(start)} – ${
    end == null ? 'Present' : formatMonthIndex(end)
  }`

export const formatMonthPeriod = (period: MonthPeriod): string =>
  formatMonthRange(period.start, period.end)

/**
 * "6 yr 6 mo", "9 mo". Stated alongside a range so nobody has to subtract two
 * dates to check their own history.
 */
export const formatMonthCount = (months: number): string => {
  const safe = Math.max(0, months)
  const years = Math.floor(safe / MONTHS_PER_YEAR)
  const remainder = safe % MONTHS_PER_YEAR
  if (!years) return `${remainder} mo`
  return remainder ? `${years} yr ${remainder} mo` : `${years} yr`
}

/**
 * "5 years 2 months", "9 months", "0 months".
 *
 * The long form, for the one place a driver reads a total rather than scans a
 * list. Abbreviations belong next to a date range where space is tight; a
 * headline figure is worth spelling out.
 */
export const formatMonthCountLong = (months: number): string => {
  const safe = Math.max(0, months)
  const years = Math.floor(safe / MONTHS_PER_YEAR)
  const remainder = safe % MONTHS_PER_YEAR
  const parts: string[] = []
  if (years) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`)
  if (remainder || !years) {
    // Nothing entered reads "0 month" — a label for an empty state rather than
    // a count of things, so it does not take the plural
    parts.push(
      `${remainder} ${remainder === 1 || safe === 0 ? 'month' : 'months'}`
    )
  }
  return parts.join(' ')
}

export const formatMonthPeriodLength = (period: MonthPeriod): string =>
  formatMonthCount(period.end - period.start + 1)

/**
 * What is still outstanding, in one unit — never two added together, and never
 * a count of months once there is a year of them ("10 years left", not "120
 * months left").
 */
export const formatMonthsRemaining = (months: number): string => {
  if (months <= 0) return 'Complete'
  if (months < MONTHS_PER_YEAR)
    return months === 1 ? '1 month left' : `${months} months left`
  const years = months / MONTHS_PER_YEAR
  const rounded = Number.isInteger(years) ? years : Number(years.toFixed(1))
  return `${rounded} ${rounded === 1 ? 'year' : 'years'} left`
}
